
import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart3, Users, Wallet, Settings2, TrendingUp,
  AlertTriangle, Clock, CheckCircle2, DollarSign,
  ChevronLeft, ChevronRight, RefreshCw, Calculator
} from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { api } from '../../../src/services/api';
import { supabase } from '../../../src/lib/supabase';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { formatCurrency, formatKwh, formatPercent } from '../../../src/lib/comissaoUtils';
import type {
  TarifaEnergia, MetaVendedor, ComissaoParceiro,
  ComissaoVendedor, Profile
} from '../../../types';
import DashboardTab from './tabs/DashboardTab';
import VendedoresTab from './tabs/VendedoresTab';
import ParceirosTab from './tabs/ParceirosTab';
import ConfiguracoesTab from './tabs/ConfiguracoesTab';

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const FolhaPagamentoView: React.FC = () => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'vendedores' | 'parceiros' | 'config'>('dashboard');
  const [initialLoading, setInitialLoading] = useState(true);

  // Period selector
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());

  // Data
  const [tarifas, setTarifas] = useState<TarifaEnergia[]>([]);
  const [comissoesVendedores, setComissoesVendedores] = useState<ComissaoVendedor[]>([]);
  const [comissoesParceiros, setComissoesParceiros] = useState<ComissaoParceiro[]>([]);
  const [vendedores, setVendedores] = useState<Profile[]>([]);
  const [parceiros, setParceiros] = useState<Profile[]>([]);
  const [todosUsuarios, setTodosUsuarios] = useState<Profile[]>([]);

  const canEdit = profile?.role === 'adm' || profile?.role === 'gerente';

  // Full data load — only shows spinner on initial mount
  const fetchData = async (showSpinner = false) => {
    if (showSpinner) setInitialLoading(true);
    try {
      const mesStr = String(mes).padStart(2, '0');
      const mesInicio = `${ano}-${mesStr}-01T00:00:00`;
      const lastDay = new Date(ano, mes, 0).getDate();
      const mesFim = `${ano}-${mesStr}-${lastDay}T23:59:59`;

      const [t, cv, cp, v, p, allUsers] = await Promise.all([
        api.folhaPagamento.getTarifas(),
        api.folhaPagamento.getComissoesVendedores(mes, ano),
        api.folhaPagamento.getComissoesParceiros(mesInicio, mesFim),
        api.folhaPagamento.getVendedores(),
        api.folhaPagamento.getParceiros(),
        (async () => {
          const { data: users } = await supabase.from('profiles').select('*').neq('role', 'consumidor');
          return users as Profile[];
        })()
      ]);
      setTarifas(t);
      setComissoesVendedores(cv);
      setComissoesParceiros(cp);
      setVendedores(v);
      setParceiros(p);
      setTodosUsuarios(allUsers || []);
    } catch (err) {
      console.error(err);
      addToast('Erro ao carregar dados da folha', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  // Lightweight refresh — only reloads the data that changes frequently
  // Does NOT trigger loading spinner → does NOT unmount/remount children
  const refreshData = async () => {
    await fetchData(false);
  };

  useEffect(() => { fetchData(true); }, [mes, ano]);

  const handleMonthChange = (dir: -1 | 1) => {
    let newMes = mes + dir;
    let newAno = ano;
    if (newMes < 1) { newMes = 12; newAno--; }
    if (newMes > 12) { newMes = 1; newAno++; }
    setMes(newMes);
    setAno(newAno);
  };

  const tabs = [
    { id: 'dashboard' as const, label: 'Dashboard', icon: BarChart3 },
    { id: 'vendedores' as const, label: 'Vendedores', icon: Users },
    { id: 'parceiros' as const, label: 'Parceiros', icon: Wallet },
    { id: 'config' as const, label: 'Configurações', icon: Settings2 },
  ];

  if (initialLoading) return <LoadingSpinner label="Carregando folha de pagamento..." />;

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-poppins font-bold dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-lg shadow-primary/20">
              <Wallet size={20} className="text-white" />
            </div>
            Folha de Pagamento
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-[52px]">Comissões de vendedores e parceiros</p>
        </div>

        {/* Month selector */}
        <div className="flex items-center gap-3 bg-white dark:bg-gray-800 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <button onClick={() => handleMonthChange(-1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <ChevronLeft size={18} className="text-gray-500 dark:text-gray-400" />
          </button>
          <span className="text-sm font-semibold text-gray-900 dark:text-white min-w-[140px] text-center">
            {MESES[mes - 1]} {ano}
          </span>
          <button onClick={() => handleMonthChange(1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <ChevronRight size={18} className="text-gray-500 dark:text-gray-400" />
          </button>
          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
          <button onClick={refreshData} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Atualizar">
            <RefreshCw size={16} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-primary to-primary-dark text-white shadow-md shadow-primary/20'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content — all tabs stay mounted so they receive prop updates */}
      <div className={activeTab === 'dashboard' ? '' : 'hidden'}>
        <DashboardTab
          comissoesVendedores={comissoesVendedores}
          comissoesParceiros={comissoesParceiros}
          mes={mes} ano={ano}
        />
      </div>
      <div className={activeTab === 'vendedores' ? '' : 'hidden'}>
        <VendedoresTab
          tarifas={tarifas}
          metas={comissoesVendedores}
          vendedores={vendedores}
          mes={mes}
          ano={ano}
          canEdit={canEdit}
          todosUsuarios={todosUsuarios}
          onRefresh={refreshData}
          comissoes={comissoesVendedores}
        />
      </div>
      <div className={activeTab === 'parceiros' ? '' : 'hidden'}>
        <ParceirosTab
          comissoes={comissoesParceiros}
          parceiros={parceiros}
          tarifas={tarifas}
          mes={mes} ano={ano}
          canEdit={canEdit}
          todosUsuarios={todosUsuarios}
          onRefresh={refreshData}
        />
      </div>
      <div className={activeTab === 'config' ? '' : 'hidden'}>
        <ConfiguracoesTab
          tarifas={tarifas}
          metas={comissoesVendedores}
          vendedores={vendedores}
          mes={mes} ano={ano}
          canEdit={canEdit}
          onRefresh={refreshData}
        />
      </div>
    </div>
  );
};

export default FolhaPagamentoView;
